import { Link } from "react-router-dom";
import { Check, Droplets } from "lucide-react";
import { METHODE_PAGE as M } from "../pages";
import { SERVICES } from "../data";
import { Reveal, SectionLabel } from "./Reveal";
import { useSeo } from "../hooks/useSeo";
import PageHero from "./PageHero";
import Stats from "./Stats";

export const MethodePage = () => {
  useSeo({ title: M.metaTitle, description: M.metaDesc });

  return (
    <div data-testid="methode-page">
      <PageHero
        title={M.heroTitle}
        subtitle={M.heroSubtitle}
        image={M.heroImage}
        crumb="Softwash Methode"
      />

      {/* Intro */}
      <section className="bg-background py-24 sm:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionLabel>Wat is Softwash</SectionLabel>
          <Reveal>
            <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-ink leading-[1.05]">
              {M.intro.title}
            </h2>
          </Reveal>
          {M.intro.paragraphs.map((p, i) => (
            <Reveal key={i} delay={0.05 * (i + 1)}>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">{p}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="relative overflow-hidden bg-navy-deep grain py-24 sm:py-28">
        <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionLabel>Het proces</SectionLabel>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white leading-[1.05]">
            {M.how.title}
          </h2>
          {M.how.paragraphs.map((p, i) => (
            <p key={i} className="mt-6 text-lg text-white/70 leading-relaxed">{p}</p>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-background py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <SectionLabel>Voordelen</SectionLabel>
            <Reveal>
              <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-ink leading-[1.05]">
                De voordelen van de Softwash methode
              </h2>
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {M.benefits.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.06}>
                <div className="h-full rounded-3xl border border-border bg-card p-7">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-teal/10 text-teal">
                    <Check className="h-6 w-6" strokeWidth={2.5} />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold text-ink">{b.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Stats />

      {/* Surfaces */}
      <section className="bg-secondary py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <SectionLabel>Alles wat vies is, krijgen wij schoon</SectionLabel>
            <Reveal>
              <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-ink leading-[1.05]">
                {M.surfaces.title}
              </h2>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {M.surfaces.groups.map((g, i) => (
              <Reveal key={g.name} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-border bg-card p-7">
                  <div className="flex items-center gap-2 text-teal">
                    <Droplets className="h-5 w-5" />
                    <h3 className="font-display text-lg font-bold text-ink">{g.name}</h3>
                  </div>
                  <ul className="mt-5 space-y-2.5">
                    {g.items.map((it) => (
                      <li key={it} className="flex items-center gap-2 text-sm text-ink/75">
                        <Check className="h-4 w-4 text-teal" strokeWidth={3} />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            {SERVICES.map((s) => (
              <Link
                key={s.id}
                to={s.path}
                className="rounded-full border border-border bg-card px-6 py-3 text-sm font-semibold text-ink transition-colors duration-300 hover:border-teal hover:text-teal"
              >
                {s.title} →
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MethodePage;
